import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";

export const HostPermissionConfirmModal = ({
  open,
  onConfirm,
  onCancel,
  participantName,
  permissionType, // 'mic' or 'video'
}) => {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[1000]" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[#060621] border border-gray-700 p-6 text-left align-middle shadow-2xl transition-all text-white">
                <Dialog.Title
                  as="h3"
                  className="text-xl font-semibold leading-6 text-white mb-2 text-center"
                >
                  Grant {permissionType === "mic" ? "Microphone" : "Camera"}{" "}
                  Access
                </Dialog.Title>
                <div className="mt-3">
                  <p className="text-sm text-gray-300 text-center">
                    Do you want to give{" "}
                    <span className="font-semibold text-white">
                      {participantName}
                    </span>{" "}
                    permission to turn on their{" "}
                    {permissionType === "mic" ? "microphone" : "camera"}?
                  </p>
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    They will receive a notification asking if they want to
                    enable their{" "}
                    {permissionType === "mic" ? "microphone" : "camera"}.
                  </p>
                </div>

                <div className="mt-6 flex gap-3 flex-col sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg w-full sm:w-1/2 bg-transparent border border-white text-white hover:bg-white/10 transition-colors"
                    onClick={onCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg w-full sm:w-1/2 bg-[#7700FF] text-white hover:opacity-90 transition-colors"
                    onClick={onConfirm}
                  >
                    Grant Access
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

/**
 * Modal shown to participant when they receive permission
 */
export const ParticipantPermissionModal = ({
  open,
  onAccept,
  onDecline,
  permissionType, // 'mic' or 'video'
}) => {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[1000]" onClose={onDecline}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[#060621] border border-gray-700 p-6 text-left align-middle shadow-2xl transition-all text-white">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 rounded-full bg-[#7700FF]/20">
                  {permissionType === "mic" ? (
                    <svg
                      className="w-6 h-6 text-[#7700FF]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-6 h-6 text-[#7700FF]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                </div>
                <Dialog.Title
                  as="h3"
                  className="text-xl font-semibold leading-6 text-white text-center mb-2"
                >
                  {permissionType === "mic" ? "Microphone" : "Camera"} Access
                  Granted
                </Dialog.Title>
                <div className="mt-3">
                  <p className="text-sm text-gray-300 text-center">
                    The host has given you permission to turn on your{" "}
                    {permissionType === "mic" ? "microphone" : "camera"}. Would
                    you like to turn it on now?
                  </p>
                </div>

                <div className="mt-6 flex justify-center gap-3 flex-col sm:flex-row">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg w-full sm:w-1/2 bg-transparent border border-white text-white hover:bg-white/10 transition-colors"
                    onClick={onDecline}
                  >
                    Not Now
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg w-full sm:w-1/2 bg-[#00C853] text-white hover:opacity-90 transition-colors"
                    onClick={onAccept}
                  >
                    Turn On
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

